import React, { useRef, useCallback } from 'react';
import { Form, notification, Skeleton } from 'antd';
import './NewOrder.scss';
import { scrollToFirstErrorField } from 'utils/commonUtils';
import ServiceSection from './subviews/serviceSection/ServiceSection';
import LaundrySection from './subviews/laundrySection/LaundrySection';
import DiscountSection from './subviews/discountSection/DiscountSection';
import TermsSection from './subviews/termsSection/TermsSection';
import FooterButtonGroup from 'components/footerButtonGroup/FooterButtonGroup';
import type {
  NewOrderFormType,
  NewOrderRequest,
  NewOrderResponse,
} from 'types/dto/order';
import { useTranslation } from 'react-i18next';
import { addOrder } from 'services/orderService';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { buildLandingPath, buildNewOrderResultPath } from 'utils/pathBuilder';
import { AxiosError } from 'axios';
import { useFormDirtyCheck } from 'hooks/useFormDirtyCheck';
import { useSession } from 'context/SessionContext';
import { MemberProfile } from 'types/dto/profile';

// Initial form values to prevent controlled/uncontrolled component warnings
const INITIAL_FORM_VALUES: Partial<NewOrderFormType> = {
  serviceTypeCode: undefined,
  pickUpMethod: undefined,
  colAddressId: 0,
  colStartTime: '',
  colEndTime: '',
  delAddressId: 0,
  delStartTime: '',
  delEndTime: '',
  collectionDateAndTimeslot: 0,
  orderDetailsItems: [],
  remark: '',
  promotionCode: '',
  useCoupon: false,
  loyaltyTokens: [],
};

const NewOrder: React.FC = () => {
  const [form] = Form.useForm<NewOrderFormType>();
  const { t } = useTranslation('new_order');
  const { t: tCommon } = useTranslation('common');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isReviewMode, setIsReviewMode] = React.useState(false);
  const navigate = useNavigate();
  const {session} = useSession();

  const containerRef = useRef<HTMLDivElement>(null);
  const [showQuantityError, setShowQuantityError] = React.useState(false);

  const [api, contextHolder] = notification.useNotification();

  // Loading options states
  const [serviceLoading, setServiceLoading] = React.useState(true);
  const [laundryLoading, setLaundryLoading] = React.useState(true);
  const [discountLoading, setDiscountLoading] = React.useState(true);
  const [serviceLoadError, setServiceLoadError] = React.useState(false);
  const [laundryLoadError, setLaundryLoadError] = React.useState(false);
  const [discountLoadError, setDiscountLoadError] = React.useState(false);
  const { profile } = useOutletContext<{ profile: MemberProfile | null }>();

  // Overall loading state
  const isLoading = serviceLoading || laundryLoading || discountLoading;

  // Setup form dirty check - only check when not in review mode
  useFormDirtyCheck({
    form,
    t:tCommon,
    enabled: !isReviewMode,
  });

  // Handle loading state changes from ServiceSection
  const handleServiceLoadingChange = useCallback((loading: boolean) => {
    setServiceLoading(loading);
  }, []);

  // Handle loading state changes from LaundrySection
  const handleLaundryLoadingChange = useCallback((loading: boolean) => {
    setLaundryLoading(loading);
  }, []);

  // Handle loading state changes from DiscountSection
  const handleDiscountLoadingChange = useCallback((loading: boolean) => {
    setDiscountLoading(loading);
  }, []);

  // Handle error state from child components
  const handleServiceLoadError = useCallback((error: boolean) => {
    setServiceLoadError(error);
  }, []);

  const handleLaundryLoadError = useCallback((error: boolean) => {
    setLaundryLoadError(error);
  }, []);

  const handleDiscountLoadError = useCallback((error: boolean) => {
    setDiscountLoadError(error);
  }, []);

  // Handle Continue button click - show review page
  const handleContinue = async () => {
    try {
      // Validate form before showing review
      await form.validateFields();

      // Check if totalCount is 0
      const formValues = form.getFieldsValue();
      const orderDetailsItems = formValues.orderDetailsItems || [];
      const totalCount = orderDetailsItems.reduce(
        (total: number, item: unknown) => {
          const qty = (item as Record<string, unknown>)?.qty || 0;
          return total + (typeof qty === 'number' ? qty : 0);
        },
        0
      );

      if (totalCount === 0) {
        console.error('Total count is 0, cannot proceed to review');

        // Show error message and scroll to it
        setShowQuantityError(true);

        // Scroll to laundry section error message
        const laundrySection = document.querySelector('.laundry-section');
        if (laundrySection) {
          laundrySection.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
        return;
      }

      // Clear error message when validation passes
      setShowQuantityError(false);

      // console.log('form values is ', formValues);
      // console.log('delStartTime:', formValues.delStartTime);
      // console.log('delEndTime:', formValues.delEndTime);
      // console.log(
      //   'collectionDateAndTimeslot:',
      //   formValues.collectionDateAndTimeslot
      // );

      //scroll to top
      if (containerRef.current) {
        containerRef.current.scrollTop = 0;
      }

      setIsReviewMode(true);
    } catch (error: unknown) {
      console.error('Form validation failed:', error);
      scrollToFirstErrorField(error, form);
    }
  };

  // Handle Back from review page
  const handleBackFromReview = () => {
    //scroll to top
    if (containerRef.current) {
      containerRef.current.scrollTop = 0;
    }
    setIsReviewMode(false);
    setShowQuantityError(false);
  };

  const handleCancel = () => {
    navigate(buildLandingPath());
  };

  // Handle final Confirm - submit order
  const handleSubmit = async () => {
    const values = form.getFieldsValue();

    // Map form values to request data
    const requestData: NewOrderRequest = {
      orderDetailsItems: (values.orderDetailsItems || []).map((item, index) => ({
        itemTypeCode: item.itemTypeCode,
        qty: item.qty || 0,
        seq: index + 1,
      })),
      // Required fields
      token: session?._tk ?? '',
      juicyAppUniqueId: profile?.loyalty_account?.unique_id ?? '',
      serviceTypeCode: values.serviceTypeCode,
      pickUpMethod: values.pickUpMethod,
      colAddressId: values.colAddressId,
      colStartTime: values.colStartTime,
      colEndTime: values.colEndTime,
      delAddressId: values.delAddressId, // TODO: mock data - should be from form
      delStartTime: values.delStartTime,
      delEndTime: values.delEndTime,
      // orderDetailsItems: values.orderDetailsItems || [],

      // Optional fields
      remark: values.remark,
      promotionCode: values.promotionCode,
      // spibili,16-Jun-2026, [Jira #29] [Admin Portal] Payment Details - Extra fields found
      useCoupon: (values.loyaltyTokens && values.loyaltyTokens.length > 0) ? true : values.useCoupon,
      loyaltyTokens: values.loyaltyTokens || [],
    };

    try {
      setIsSubmitting(true);
      // console.log('Form submitted:', requestData);

      const response: NewOrderResponse = await addOrder(
        '/customer',
        requestData
      );
      if (response.result === 'OK') {
        navigate(buildNewOrderResultPath(), {
          state: {
            reservationNo: response.reservationNo,
            orderID: response.orderID
          },
        });
      } else {
        api.error({
          message: t('createOrderFailed'),
          description: response.result ?? tCommon('error.unknownError'),
        });
      }
    } catch (error: unknown) {
      api.error({
        message: t('createOrderFailed'),
        description: (error as AxiosError)?.message ?? tCommon('error.unknownError'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="new-order-container" ref={containerRef}>
      {contextHolder}
      <div className="new-order-content">

        {/* Show loading skeleton overlay while data is loading */}
        {isLoading && (
          <div>
            <Skeleton active paragraph={{ rows: 4 }} />
          </div>
        )}

        {/* Form content - always mounted but hidden when loading */}
        <Form<NewOrderFormType>
          form={form}
          layout="vertical"
          className="new-order-form"
          style={{ display: isLoading ? 'none' : 'block' }}
          initialValues={INITIAL_FORM_VALUES}
        >
          <div className={'form-content'}>
            {/* Pick up service details Section */}
            {serviceLoadError ? (
              <div className="load-data-error">
                {tCommon('error.loadDataError')}
              </div>
            ) : (
              <ServiceSection
                isReadonly={isReviewMode}
                onLoadingChange={handleServiceLoadingChange}
                onLoadError={handleServiceLoadError}
              />
            )}

            {/* Laundry details Section */}
            {laundryLoadError ? (
              <div className="load-data-error">
                {tCommon('error.loadDataError')}
              </div>
            ) : (
              <LaundrySection
                isReadonly={isReviewMode}
                showQuantityError={showQuantityError}
                onLoadingChange={handleLaundryLoadingChange}
                onLoadError={handleLaundryLoadError}
              />
            )}

            {/* Payment discounts Section */}
            {discountLoadError ? (
              <div className="load-data-error">
                {tCommon('error.loadDataError')}
              </div>
            ) : (
              <DiscountSection
                isReadonly={isReviewMode}
                onLoadingChange={handleDiscountLoadingChange}
                onLoadError={handleDiscountLoadError}
              />
            )}

            <TermsSection isReadonly={isReviewMode} />

            <FooterButtonGroup
              onContinue={isReviewMode ? handleSubmit : handleContinue}
              onCancel={isReviewMode ? handleBackFromReview : handleCancel}
              isSubmitting={isSubmitting}
              cancelText={isReviewMode ? tCommon('button.back') : tCommon('button.cancel')}
              continueText={tCommon('button.continue')}
            />
          </div>
        </Form>
      </div>
    </div>
  );
};

export default NewOrder;
